import React, { PropsWithChildren, ReactNode, useMemo, MouseEventHandler, CSSProperties } from 'react';
import { cx } from '@linaria/atomic';
import { pick } from 'lodash-es';
import { useQuery } from 'react-query';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import {
  AppLink,
  constructRoutePath,
  SettingsLink,
  settingsRouteLookup,
  SettingsRoutes,
  StaticSettingsRoutes,
  useSettingsPathLabelMap,
} from '@frontend/settings-routing';
import { extractAndFormatPathName } from '@frontend/string';
import { AnyParams, useMatch, useNavigate } from '@frontend/weave-router';
import {
  BreadcrumbPathProps,
  Breadcrumbs,
  Chip,
  SkeletonLoader,
  SpinningLoader,
  Text,
  TextLink,
} from '@frontend/design-system';
import { PageTitle } from '../page-title';
import { ExpandText } from './expand-text';
import {
  pageStyle,
  pageSettingsStyle,
  pageHeader,
  pageContent,
  loadingContainerStyle,
  breadcrumbsSpacingStyle,
  headerContentStyle,
  headerTitleStyle,
  subtitleTextStyle,
  subtitleContainerStyle,
  expandTextStyle,
  headerActionStyle,
  settingsChipContainerStyle,
  settingsChipActionStyle,
  skeletonLoaderStyle,
} from './page.styles';

export type SettingsBreadcrumbs = {
  to?: SettingsRoutes;
  params?: Record<string, string>;
  label: string;
  onClick?: MouseEventHandler<HTMLElement>;
  currentPage?: boolean;
  search?: Record<string, string>;
};

type MaxWidthProp = string | number | null;

export type PageProps<IsSettings extends boolean = false> = PropsWithChildren<{
  action?: React.ReactNode;
  breadcrumbs?: IsSettings extends false ? BreadcrumbPathProps[] : SettingsBreadcrumbs[];
  children?: ReactNode;
  className?: string;
  customBackUrl?: string;
  customBackFn?: () => void;
  icon?: string;
  id?: string;
  loading?: boolean;
  maxWidth?: MaxWidthProp;
  moreSubtitle?: string;
  showBackBtn?: boolean;
  style?: Record<string, any>;
  subtitle?: string | React.ReactNode;
  title?: React.ReactNode;
  isSettingsPage?: IsSettings;
  customHeader?: React.ReactNode;
  settingsLocationConfig?: SettingsLocationChipProps;
  showHeader?: boolean;
}>;

const getMaxWidthValue = (val: MaxWidthProp): string => {
  if (val === null) {
    return '1000px';
  } else if (typeof val === 'number') {
    return `${val}px`;
  }
  return val;
};

const renderBreadcrumbs = (isSettingsPage?: boolean, breadcrumbs?: BreadcrumbPathProps[] | SettingsBreadcrumbs[]) => {
  if (isSettingsPage) {
    return breadcrumbs ? (
      <SettingsBreadcrumbs breadcrumbs={breadcrumbs as SettingsBreadcrumbs[]} />
    ) : (
      <AutoSettingsBreadcrumbs />
    );
  }

  return (
    breadcrumbs && (
      <Breadcrumbs className={breadcrumbsSpacingStyle}>
        {breadcrumbs.map(({ to, label, ...rest }) => (
          <Breadcrumbs.Path key={label} as={AppLink} to={to} {...rest}>
            {label}
          </Breadcrumbs.Path>
        ))}
      </Breadcrumbs>
    )
  );
};

/**
 * @deprecated Instead, please use `<Page>` from `@frontend/page`
 * For examples, see:
 * - `frontend/libs/ui/components/settings/settings-page/settings-page.tsx`
 * - `frontend/libs/features/payments/src/components/PaymentsPage/index.tsx`
 */
export const Page = <IsSettings extends boolean = false>({
  action,
  breadcrumbs,
  children,
  className,
  customBackUrl,
  customBackFn,
  customHeader,
  id,
  loading,
  maxWidth = 1400,
  moreSubtitle,
  showBackBtn = false,
  subtitle,
  title,
  isSettingsPage,
  settingsLocationConfig,
  style,
  ...rest
}: PageProps<IsSettings>) => {
  const navigate = useNavigate();
  if (loading) {
    return (
      <div className={loadingContainerStyle}>
        <SpinningLoader size='xl' />
      </div>
    );
  }

  let onBackClick: () => void = () => history.go(-1);
  if (customBackFn) {
    onBackClick = customBackFn;
  } else if (customBackUrl) {
    onBackClick = () => navigate({ to: customBackUrl });
  }

  const shouldShowHeaderContent = !!title || !!subtitle || !!action;

  return (
    <div
      className={cx(pageStyle, isSettingsPage && pageSettingsStyle, className)}
      style={{ ...style, '--page-max-width': getMaxWidthValue(maxWidth) } as CSSProperties}
      id={id}
      {...rest}
    >
      {customHeader ? (
        customHeader
      ) : (
        <header className={cx(pageHeader, 'page-header')}>
          {renderBreadcrumbs(isSettingsPage, breadcrumbs)}
          {shouldShowHeaderContent && (
            <HeaderContent
              title={title}
              subtitle={subtitle}
              moreSubtitle={moreSubtitle}
              action={action}
              showBackBtn={showBackBtn}
              onBackClick={onBackClick}
              isSettingsPage={isSettingsPage}
              settingsLocationConfig={settingsLocationConfig}
            />
          )}
        </header>
      )}
      <div className={pageContent}>{children}</div>
    </div>
  );
};

// For pages without navigation
export const SitePage = <IsSettings extends boolean = false>({
  action,
  children,
  className,
  id,
  loading,
  maxWidth = 1400,
  moreSubtitle,
  customHeader,
  subtitle,
  title,
  style,
  ...rest
}: PageProps<IsSettings>) => {
  if (loading) {
    return (
      <div className={loadingContainerStyle}>
        <SpinningLoader size='xl' />
      </div>
    );
  }

  const shouldShowHeaderContent = !!title || !!subtitle || !!action;

  return (
    <div
      className={cx(pageStyle, className)}
      style={{ ...style, '--page-max-width': getMaxWidthValue(maxWidth) } as CSSProperties}
      id={id}
      {...rest}
    >
      {customHeader ? (
        customHeader
      ) : (
        <header className={cx(pageHeader, 'page-header')}>
          {shouldShowHeaderContent && (
            <HeaderContent
              title={title}
              subtitle={subtitle}
              moreSubtitle={moreSubtitle}
              action={action}
              showBackBtn={false}
              isSettingsPage={false}
            />
          )}
        </header>
      )}
      <div className={pageContent}>{children}</div>
    </div>
  );
};

export const HeaderContent = <IsSettings extends boolean = false>({
  action,
  moreSubtitle,
  showBackBtn = false,
  onBackClick,
  subtitle,
  title,
  isSettingsPage,
  settingsLocationConfig,
}: PageProps<IsSettings> & { onBackClick?: () => void }) => {
  return (
    <div className={headerContentStyle}>
      <div className={headerTitleStyle}>
        {typeof title === 'string' ? (
          <PageTitle title={title} showBackBtn={showBackBtn && history.length > 0} onBackClick={onBackClick} />
        ) : (
          title
        )}
        {isSettingsPage && !settingsLocationConfig?.hideLocationChip && (
          <SettingsLocationChip {...settingsLocationConfig} />
        )}
      </div>
      {subtitle && typeof subtitle === 'string' && (
        <Text className={subtitleTextStyle}>
          <ExpandText className={expandTextStyle} text={subtitle || ''} hiddenText={moreSubtitle} />
        </Text>
      )}
      {subtitle && typeof subtitle !== 'string' && (
        <div className={subtitleContainerStyle}>
          <ExpandText className={expandTextStyle} text={subtitle || ''} hiddenText={moreSubtitle} />
        </div>
      )}

      {action && <div className={cx(headerActionStyle, 'page-action')}>{action}</div>}
    </div>
  );
};

type SettingsLocationChipProps = {
  isSingleLocation?: boolean;
  chipLabel?: string;
  onActionClick?: React.MouseEventHandler<HTMLElement> & React.MouseEventHandler<HTMLAnchorElement>;
  actionLabel?: string;
  hideLocationChip?: boolean;
};

// TODO: this need to be removed as we have same component in libs/ui/page/src/atoms/header/settings-location-chip.tsx
const SettingsLocationChip = ({
  isSingleLocation,
  chipLabel,
  onActionClick,
  actionLabel,
}: SettingsLocationChipProps) => {
  const { t } = useTranslation('base');
  const { selectedLocationIds, getLocationName, isSingleTypeScope } = useAppScopeStore();

  if (isSingleTypeScope) return null;

  return (
    <div className={settingsChipContainerStyle}>
      {isSingleLocation ?? selectedLocationIds.length === 1 ? (
        <Chip.SingleChip>{chipLabel || getLocationName(selectedLocationIds[0])}</Chip.SingleChip>
      ) : (
        <Chip.MultiChip>{chipLabel || t(`{{count}} Locations`, { count: selectedLocationIds.length })}</Chip.MultiChip>
      )}
      {onActionClick ? (
        <TextLink
          weight='bold'
          className={settingsChipActionStyle}
          onClick={(e) => {
            onActionClick?.(e);
          }}
        >
          {actionLabel ?? t('Edit')}
        </TextLink>
      ) : null}
    </div>
  );
};

const SettingsBreadcrumbs = ({ breadcrumbs }: { breadcrumbs: SettingsBreadcrumbs[] }) => (
  <Breadcrumbs className={breadcrumbsSpacingStyle}>
    {breadcrumbs.map(({ to, label, params, ...rest }) => (
      <Breadcrumbs.Path key={label} as={SettingsLink} to={to as SettingsRoutes} params={params as any} {...rest}>
        {label}
      </Breadcrumbs.Path>
    ))}
  </Breadcrumbs>
);

function extractParams(path: string, params: Record<string, string>): Record<string, string> | undefined {
  if (!path.includes(':')) {
    return undefined;
  }

  const extractedKeys = path.split('/').reduce((keys, segment) => {
    if (segment.startsWith(':')) {
      keys.push(segment.substring(1));
    }
    return keys;
  }, [] as string[]);

  return pick(params, extractedKeys);
}

const AutoSettingsBreadcrumbs = () => {
  const match = useMatch({ strict: false });
  const breadcrumbs = useMemo(
    () => settingsRouteLookup.searchPath(('/settings' + match.pathname) as string),
    [match.pathname]
  );

  if (breadcrumbs.length === 1) return null;

  return (
    <Breadcrumbs className={breadcrumbsSpacingStyle}>
      {breadcrumbs.map((crumb) => (
        <AutoSettingsLabel crumb={crumb.replace('/settings', '')} params={match.params} key={crumb} />
      ))}
    </Breadcrumbs>
  );
};

type AutoSettingsLabelProps = { params: AnyParams; crumb: string };

type AsyncSettingsLabel = AutoSettingsLabelProps;

const AsyncSettingsLabel = ({ params, crumb }: AsyncSettingsLabel) => {
  const pathLabelMap = useSettingsPathLabelMap();

  const fetchSettingsLabel = async () => {
    const mappedLabel = pathLabelMap[crumb as keyof typeof pathLabelMap]?.label;
    if (typeof mappedLabel === 'function') {
      try {
        return await mappedLabel(params as any);
      } catch (error) {
        const label = extractAndFormatPathName(crumb);
        return label.includes(':') ? params[label.slice(1)] : label;
      }
    }

    return '';
  };

  const { data: asyncLabel, isLoading } = useQuery(
    ['auto-settings-label', constructRoutePath(crumb, params)],
    fetchSettingsLabel,
    {
      cacheTime: 5 * 60 * 1000,
      staleTime: 0,
    }
  );

  return isLoading ? (
    <SkeletonLoader className={skeletonLoaderStyle} animation='shimmer' height={14} width={50} />
  ) : (
    <>{asyncLabel}</>
  );
};

const AutoSettingsLabel = ({ params, crumb, currentPage }: AutoSettingsLabelProps & { currentPage?: boolean }) => {
  const pathLabelMap = useSettingsPathLabelMap();

  const labelContent = pathLabelMap[crumb as keyof typeof pathLabelMap]?.label;
  const isLabelFunction = typeof labelContent === 'function';

  return (
    <Breadcrumbs.Path
      key={crumb as StaticSettingsRoutes}
      as={SettingsLink}
      to={crumb as StaticSettingsRoutes}
      params={extractParams(crumb, params) as any}
      currentPage={currentPage}
    >
      {isLabelFunction ? (
        <AsyncSettingsLabel params={params} crumb={crumb} />
      ) : (
        labelContent || extractAndFormatPathName(crumb)
      )}
    </Breadcrumbs.Path>
  );
};

AutoSettingsLabel.displayName = 'BreadcrumbsPath';
